/**
 * Routeur hash minimal, sans dépendance. On reste en hash (#/maisons) plutôt
 * qu'en history API : l'app est servie en statique (Caddy) sans règle de
 * rewrite SPA garantie — le hash évite tout 404 au refresh / partage de lien.
 */
import { useEffect, useState } from 'react';
import type { AnchorHTMLAttributes, ReactNode } from 'react';

function currentPath(): string {
  const h = window.location.hash.slice(1);
  return h && h.startsWith('/') ? h : '/';
}

/** Retourne le chemin courant (ex: "/", "/maisons", "/maisons/ciss-230-1"). */
export function useRoute(): string {
  const [path, setPath] = useState<string>(currentPath);
  useEffect(() => {
    const onChange = () => setPath(currentPath());
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  return path;
}

/** Navigation impérative. Remonte en haut de page (nouvelle "page"). */
export function navigate(to: string): void {
  if (currentPath() === to) return;
  window.location.hash = to;
  window.scrollTo(0, 0);
}

interface LinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  to: string;
  children: ReactNode;
}

/** Lien interne (ancre #) — navigation hash + reset scroll. */
export function Link({ to, children, onClick, ...rest }: LinkProps) {
  return (
    <a
      href={`#${to}`}
      onClick={(e) => {
        onClick?.(e);
        if (e.defaultPrevented) return;
        // Laisse le comportement par défaut gérer le hash, mais reset le scroll.
        window.scrollTo(0, 0);
      }}
      {...rest}
    >
      {children}
    </a>
  );
}

/** Extrait le segment :id d'une route "/maisons/:id". null si pas une fiche. */
export function matchMaisonId(path: string): string | null {
  const m = path.match(/^\/maisons\/(.+)$/);
  return m ? decodeURIComponent(m[1]) : null;
}
